/**
 * The stored pronoun codes, shared by the stylist and client records:
 * 1 She/Her, 2 He/Him, 3 They/Them, 4 the person's own text. The labels are
 * domain data, so they belong in one place rather than beside each model.
 */
const PRONOUN_LABELS: Record<number, string> = {
  1: 'She/Her',
  2: 'He/Him',
  3: 'They/Them',
};

/**
 * Pronouns as a person reads them. Anywhere a human sees the value — support
 * chat, a profile header — wants this, never the raw code. An unset code, the
 * custom code with no text, and a code we don't know all read as empty.
 *
 * Client.getReadablePronouns() still has its own copy, which returns undefined
 * for an unknown code. Only one call site can tell the difference —
 * booking-detail.component.html:29 tests `!= ''`, and `undefined != ''` is
 * true, so an unset code renders an empty "()" there. Pointing Client at this
 * helper would remove those stray parens rather than break anything; it is
 * left alone only because that is a visible change this branch did not set out
 * to make.
 */
export function readablePronouns(
  code: number | null | undefined,
  custom: string | null | undefined
): string {
  // Indexing would coerce a string '1' to She/Her; the switch this replaced
  // used === and rejected it. Keep that strictness — the API types the column
  // as a number and a string here would mean something upstream went wrong.
  if (typeof code !== 'number') {
    return '';
  }
  if (code === 4) {
    return custom || '';
  }
  return PRONOUN_LABELS[code] || '';
}
